// Constants for order statuses and refund statuses
export const ORDER_STATUS = {
  ALL: 999,
  PRE_COMMITED: 0,
  OPENED: 1,
  IN_PROGRESS: 2,
  DELIVERY: 3,
  READY: 4,
  FINISHED: 5,
  CANCELLATION_REQUEST: 6,
  CANCELLED: 7,
} as const;

export const REFUND_STATUS = {
  NEW: 0,
  IN_PROGRESS: 1,
  APPROVED: 2,
  REJECTED: 3,
} as const;

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];
export type RefundStatus = (typeof REFUND_STATUS)[keyof typeof REFUND_STATUS];

// Interface for text and color scheme properties of statuses
interface StatusProps {
  text: string;
  colorScheme: string;
}

// Mapping for order statuses with text and color schemes
export const orderStatusMap: Record<OrderStatus, StatusProps> = {
  [ORDER_STATUS.ALL]: { text: 'Все', colorScheme: 'transparent' },
  [ORDER_STATUS.PRE_COMMITED]: { text: 'Не подтвержден', colorScheme: 'gray' },
  [ORDER_STATUS.OPENED]: { text: 'Открыт', colorScheme: 'green' },
  [ORDER_STATUS.IN_PROGRESS]: { text: 'В работе', colorScheme: 'orange' },
  [ORDER_STATUS.DELIVERY]: { text: 'Доставка', colorScheme: 'blue' },
  [ORDER_STATUS.READY]: { text: 'Готов к выдаче', colorScheme: 'cyan' },
  [ORDER_STATUS.FINISHED]: { text: 'Завершен', colorScheme: 'green' },
  [ORDER_STATUS.CANCELLATION_REQUEST]: { text: 'Запрос на отмену', colorScheme: 'yellow' },
  [ORDER_STATUS.CANCELLED]: { text: 'Отменен', colorScheme: 'red' },
};

// Mapping for refund statuses with text and color schemes
export const refundStatusMap: Record<RefundStatus, StatusProps> = {
  [REFUND_STATUS.NEW]: { text: 'Новый', colorScheme: 'blue' },
  [REFUND_STATUS.IN_PROGRESS]: { text: 'В обработке', colorScheme: 'orange' },
  [REFUND_STATUS.APPROVED]: { text: 'Подтвержден', colorScheme: 'green' },
  [REFUND_STATUS.REJECTED]: { text: 'Отклонен', colorScheme: 'red' },
};

// Default status for unknown cases
export const defaultStatus: StatusProps = {
  text: 'Неизвестный статус',
  colorScheme: 'gray',
};

// Selectors
import { createSelector } from 'reselect';
import { RootState } from '../../store/store';
import { OrderDTO } from './types';

// Base selectors for orders
const ordersListSelector = (state: RootState) => state.orders.list;
const currentOrderSelector = (state: RootState) => state.orders.currentOrder;
const isOrdersLoadingSelector = (state: RootState) => state.orders.isLoading.list;
const isOrderLoadingSelector = (state: RootState) => state.orders.isLoading.single;
const ordersErrorSelector = (state: RootState) => state.orders.errors.list;
const orderErrorSelector = (state: RootState) => state.orders.errors.single;

// Selectors for orders
export const selectAllOrders = createSelector(ordersListSelector, list => list || []);
export const selectCurrentOrder = createSelector(
  currentOrderSelector,
  currentOrder => currentOrder,
);
export const selectIsLoadingOrders = createSelector(
  isOrdersLoadingSelector,
  isLoading => isLoading,
);
export const selectIsLoadingOrder = createSelector(isOrderLoadingSelector, isLoading => isLoading);
export const selectOrdersError = createSelector(ordersErrorSelector, error => error || '');
export const selectOrderError = createSelector(orderErrorSelector, error => error || '');

// Selector for filtering orders by status
export const selectOrdersByStatus = (status: number) =>
  createSelector(ordersListSelector, list =>
    (list || []).filter((order: OrderDTO) => order.status === status),
  );

// Selector for filtering orders by status, date range, and search term
export const selectFilteredOrders = (
  status: OrderStatus | 'all',
  startDate: Date | null,
  endDate: Date | null,
  searchTerm: string,
) =>
  createSelector(ordersListSelector, orders => {
    return (orders || []).filter((order: OrderDTO) => {
      const matchesStatus = status === ORDER_STATUS.ALL || order.status === status;

      const orderDate = new Date(order.created_at);
      const matchesStartDate = startDate ? orderDate >= startDate : true;
      const matchesEndDate = endDate ? orderDate <= endDate : true;

      const matchesSearchTerm = order.order_items.some(item =>
        item.good_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      return matchesStatus && matchesStartDate && matchesEndDate && matchesSearchTerm;
    });
  });

// Selector for filtering orders based on tab selection, date range, and search term
export const selectFilteredOrdersByTab = (
  tab: OrderStatus,
  startDate: Date | null,
  endDate: Date | null,
  searchTerm: string,
) =>
  createSelector(ordersListSelector, orders => {
    return (orders || []).filter((order: OrderDTO) => {
      let matchesTab = true;

      console.log(String(order.status) === '-1', 'order');

      // Tab-based filtering logic
      switch (tab) {
        case ORDER_STATUS.IN_PROGRESS:
          matchesTab = order.status === ORDER_STATUS.IN_PROGRESS;
          break;
        case ORDER_STATUS.CANCELLATION_REQUEST:
          matchesTab = order.status === ORDER_STATUS.CANCELLATION_REQUEST;
          break;
        case ORDER_STATUS.CANCELLED:
          matchesTab = order.status === ORDER_STATUS.CANCELLED;
          break;
        case ORDER_STATUS.DELIVERY:
          matchesTab = order.status === ORDER_STATUS.DELIVERY;
          break;
        case ORDER_STATUS.READY:
          matchesTab = order.status === ORDER_STATUS.READY;
          break;
        default:
          console.log(order, 'all');
          matchesTab = true; // Show all for "Все"
      }

      // Filter by date range
      const orderDate = new Date(order.created_at);
      const matchesStartDate = startDate ? orderDate >= startDate : true;
      const matchesEndDate = endDate ? orderDate <= endDate : true;

      // Filter by search term
      const matchesSearchTerm = order.order_items.some(item =>
        item.good_name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      return matchesTab && matchesStartDate && matchesEndDate && matchesSearchTerm;
    });
  });
